import * as React from 'react';

import { get as getUser } from 'js/lib/user';

import {
  isEmbeddedCoachForEnterpriseEnabled,
  useProductFeatures,
} from 'bundles/ai-course-coach/utils/productFeatureFlags';
import { captureMessageInSentry } from 'bundles/ai-course-coach/utils/sentryUtils';
import { IdTypes } from 'bundles/product-features';
import type { IProductFeatureSet, Scope } from 'bundles/product-features/private/types';

type Props = {
  embeddedCoachEnabled: boolean;
  enterpriseEpicCheck: () => boolean;
  defaultCoachEnabled: boolean;
  children: (props: { showEmbeddedCoach: boolean; showDefaultCoach: boolean }) => JSX.Element | null;
};

type enterpriseCoachFeaturesScope = Scope<{
  enterprise: {
    enableAiCoach: unknown;
  };
}>;

type CoachEnabledFeatureEarlyAccessProps = {
  features: IProductFeatureSet<enterpriseCoachFeaturesScope>;
  embeddedCoachEnabled: boolean;
  enterpriseEpicCheck: () => boolean;
};

const coachEnabledFeatureEarlyAccess = ({
  features,
  embeddedCoachEnabled,
  enterpriseEpicCheck = isEmbeddedCoachForEnterpriseEnabled.preview,
}: CoachEnabledFeatureEarlyAccessProps) => {
  const isCoachEnterpriseEnabled = features?.get?.('enterprise', 'enableAiCoach');

  return {
    coachEnabledByEpic: embeddedCoachEnabled,
    coachEnabledByEnterpriseFeature: isCoachEnterpriseEnabled,
    coachEnabledByEnterpriseEpic: isCoachEnterpriseEnabled && enterpriseEpicCheck(),
  };
};

const CoachPermissionsWrapper: React.FC<Props> = ({
  defaultCoachEnabled,
  embeddedCoachEnabled,
  enterpriseEpicCheck = isEmbeddedCoachForEnterpriseEnabled.preview,
  children,
}) => {
  const { showEmbeddedCoach, showDefaultCoach } = useCoachPermissions({
    defaultCoachEnabled,
    embeddedCoachEnabled,
    enterpriseEpicCheck,
  });

  return children({ showEmbeddedCoach, showDefaultCoach });
};

function useCoachPermissions({
  defaultCoachEnabled,
  embeddedCoachEnabled,
  enterpriseEpicCheck,
}: {
  defaultCoachEnabled: boolean;
  embeddedCoachEnabled: boolean;
  enterpriseEpicCheck: () => boolean;
}) {
  const userId = String(getUser().id);
  const [showEmbeddedCoach, setShowEmbeddedCoach] = React.useState<boolean>(false);
  const [showDefaultCoach, setShowDefaultCoach] = React.useState<boolean>(false);

  const { loading, error, features } = useProductFeatures({ idType: IdTypes.User, id: userId });

  React.useEffect(() => {
    if (loading || error || !features) {
      if (error) {
        captureMessageInSentry({ error });
      }
      return;
    }

    const { coachEnabledByEpic, coachEnabledByEnterpriseFeature, coachEnabledByEnterpriseEpic } =
      coachEnabledFeatureEarlyAccess({ features, embeddedCoachEnabled, enterpriseEpicCheck });

    // default coach enabled is a flag passed in from the original coach users epic,
    // all of those users should see the default version of coach.
    if (defaultCoachEnabled) {
      setShowDefaultCoach(true);
      setShowEmbeddedCoach(false);
      return;
    }

    // all enterprise users should see some version of coach IF the feature toggle is enabled
    if (coachEnabledByEnterpriseFeature) {
      if (coachEnabledByEnterpriseEpic) {
        setShowEmbeddedCoach(true);
        setShowDefaultCoach(false);
      } else {
        setShowDefaultCoach(true);
        setShowEmbeddedCoach(false);
      }
      return;
    }

    // all consumer users that have never seen coach before, we will rely on
    // the epic response for embedded coach experience to determine if we
    // should show this or not to Consumer users
    if (coachEnabledByEpic) {
      setShowEmbeddedCoach(true);
      setShowDefaultCoach(false);
      return;
    }

    setShowEmbeddedCoach(false);
    setShowDefaultCoach(false);
  }, [loading, error, features, defaultCoachEnabled, embeddedCoachEnabled, enterpriseEpicCheck]);

  return { showEmbeddedCoach, showDefaultCoach };
}

export { CoachPermissionsWrapper, useCoachPermissions };
